// src/pages/ApplyLanding.tsx: public account-type chooser at /apply.
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { C } from '../lib/applications';
import { MANAGER_FROM_PRICE, PROVIDER_FROM_PRICE } from '../lib/onboardingApplications';
import { AltvenoMark } from '../components/ApplyShell';

interface Choice {
  key: string;
  accent: string;
  title: string;
  description: string;
  badge: string;
  cta: string;
  to: string;
  icon: React.ReactNode;
}

const stroke = (d: string, colour: string) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colour}
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const PURPLE = '#6D28D9';

const CHOICES: Choice[] = [
  {
    key: 'manager',
    accent: C.navy,
    title: 'I manage a fund',
    description: 'List your fund, connect with allocators, manage DDQ and benchmark your performance.',
    badge: `From ${MANAGER_FROM_PRICE}/year`,
    cta: 'Apply as a Manager',
    to: '/apply/manager',
    icon: stroke('M3 3v18h18M7 15l3-4 3 3 5-7', C.navy),
  },
  {
    key: 'allocator',
    accent: C.teal,
    title: 'I allocate capital',
    description: 'Discover alternative managers, manage due diligence and track your deal flow pipeline.',
    badge: 'Always Free',
    cta: 'Apply as an Allocator',
    to: '/apply/allocator',
    icon: stroke('M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm14 0a4 4 0 1 1-8 0 4 4 0 0 1 8 0z', C.teal),
  },
  {
    key: 'provider',
    accent: PURPLE,
    title: 'I provide services',
    description: 'List your firm on the marketplace and connect with alternative investment managers and allocators.',
    badge: `From ${PROVIDER_FROM_PRICE}/year`,
    cta: 'Apply as a Service Provider',
    to: '/apply/provider',
    icon: stroke('M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z', PURPLE),
  },
  {
    key: 'candidate',
    accent: C.amber,
    title: 'I am looking for a role',
    description: 'Find your next position in the alternatives industry and connect with leading managers.',
    badge: 'Free to Join',
    cta: 'Apply as a Candidate',
    to: '/apply/candidate',
    icon: stroke('M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', C.amber),
  },
];

export default function ApplyLanding() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: C.white, fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ background: C.navy, padding: '22px 32px 34px' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '20px' }}>
            <AltvenoMark />
            <span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '18px', color: C.white }}>Altveno</span>
          </div>
          <h1 style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '28px', color: C.white, fontWeight: 400, margin: '0 0 8px', letterSpacing: '-0.3px' }}>
            Apply for Platform Access
          </h1>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.6 }}>
            Choose the account type that fits you. Each application takes about five minutes.
          </p>
        </div>
      </div>

      {/* ── Choices ── */}
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '32px 24px 56px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: '16px' }}>
          {CHOICES.map(choice => (
            <div
              key={choice.key}
              onClick={() => navigate(choice.to)}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; }}
              style={{
                background: C.white,
                border: `1px solid ${C.border}`,
                borderTop: `3px solid ${choice.accent}`,
                borderRadius: '4px',
                padding: '24px 26px',
                cursor: 'pointer',
                transition: 'border-color 0.15s',
                display: 'flex', flexDirection: 'column', gap: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '4px',
                  background: `${choice.accent}12`, border: `1px solid ${choice.accent}33`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>{choice.icon}</div>
                <span style={{
                  background: `${choice.accent}10`, color: choice.accent,
                  border: `1px solid ${choice.accent}40`, borderRadius: '2px',
                  fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.6px',
                  textTransform: 'uppercase', padding: '3px 9px', whiteSpace: 'nowrap',
                }}>{choice.badge}</span>
              </div>

              <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '19px', color: C.navy, lineHeight: 1.3 }}>
                {choice.title}
              </div>
              <p style={{ fontSize: '13px', color: C.muted, lineHeight: 1.65, margin: 0, flex: 1 }}>
                {choice.description}
              </p>

              <Link
                to={choice.to}
                onClick={e => e.stopPropagation()}
                style={{
                  marginTop: '6px', textAlign: 'center', textDecoration: 'none',
                  background: C.navy, color: C.white, borderRadius: '3px',
                  padding: '10px 18px', fontSize: '13px', fontWeight: 600,
                  fontFamily: 'system-ui', display: 'block',
                }}
              >{choice.cta}</Link>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '32px', fontSize: '13px', color: C.muted }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: C.teal, textDecoration: 'none', fontWeight: 600 }}>Sign in</Link>
        </div>
      </div>
    </div>
  );
}
